import React, { createContext, useContext, useState } from "react";

import { useMarketplacesQuery } from "@/graphql/generated";
import { marketplaceLogo } from "@/lib/marketplaceLogo";

interface MarketplaceItem {
  id: string;
  code: string;
  name: string;
  logo: string;
  status: string;
  syncStatus: string;
  lastSyncedAt: string | null;
  websiteUrl: string | null;
  /** Marketplace-specific sync config from the server (spApiMarketplaceId, etc.) */
  syncConfig: Record<string, unknown> | null;
}

interface CatalogContextType {
  isLoading: boolean;
  currentMarketplaces: MarketplaceItem[];
  activeMarketplaces: MarketplaceItem[];
  selectedMarketplaceId: string | null;
  selectedMarketplace: MarketplaceItem | undefined;
  selectMarketplace: (marketplaceId: string) => void;
}

const CatalogContext = createContext<CatalogContextType | undefined>(undefined);

export const CatalogProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [selectedMarketplaceId, setSelectedMarketplaceId] = useState<
    string | null
  >(null);

  const { data, isLoading } = useMarketplacesQuery();

  const STATUS_ORDER: Record<string, number> = {
    ACTIVE: 0,
    COMING_SOON: 1,
    INACTIVE: 2,
  };

  const currentMarketplaces: MarketplaceItem[] = (data?.marketplaces ?? [])
    .filter((mp) => mp.status !== "DEPRECATED")
    .map((mp) => ({
      id: mp.code,
      code: mp.code,
      name: mp.name,
      logo: marketplaceLogo(mp.code),
      status: mp.status,
      syncStatus: mp.syncStatus,
      lastSyncedAt: mp.lastSyncedAt ?? null,
      websiteUrl: mp.websiteUrl ?? null,
      syncConfig: (mp.syncConfig ?? null) as Record<string, unknown> | null,
    }))
    .sort(
      (a, b) => (STATUS_ORDER[a.status] ?? 99) - (STATUS_ORDER[b.status] ?? 99),
    );

  const activeMarketplaces = currentMarketplaces.filter(
    (mp) => mp.status === "ACTIVE" || mp.status === "COMING_SOON",
  );

  const selectedMarketplace = currentMarketplaces.find(
    (mp) => mp.id === selectedMarketplaceId,
  );

  const selectMarketplace = (marketplaceId: string) => {
    setSelectedMarketplaceId(marketplaceId);
  };

  return (
    <CatalogContext.Provider
      value={{
        isLoading,
        activeMarketplaces,
        currentMarketplaces,
        selectedMarketplaceId,
        selectedMarketplace,
        selectMarketplace,
      }}
    >
      {children}
    </CatalogContext.Provider>
  );
};

export const useCatalog = (): CatalogContextType => {
  const context = useContext(CatalogContext);
  if (!context) {
    throw new Error("useCatalog must be used within a CatalogProvider");
  }
  return context;
};
